import { useCallback, useContext, useEffect, useState } from "react";
import { Card, Menu } from "../../../components";
import { Button, Dropdown } from "@humansignal/ui";
import { ApiContext } from "../../../providers/ApiProvider";
import { StorageSummary } from "./StorageSummary";
import { StorageHealth } from "./StorageHealth";
import { IconEllipsisVertical } from "@humansignal/icons";
import { useTranslation } from "../../../i18n/LocaleProvider";

export const StorageCard = ({ rootClass, target, storage, onEditStorage, onDeleteStorage, storageTypes }) => {
  const { t } = useTranslation();
  const [syncing, setSyncing] = useState(false);
  const api = useContext(ApiContext);
  const [storageData, setStorageData] = useState({ ...storage });
  const [synced, setSynced] = useState(null);

  const startSync = useCallback(async () => {
    setSyncing(true);
    setSynced(null);

    const result = await api.callApi("syncStorage", {
      params: {
        target,
        type: storageData.type,
        pk: storageData.id,
      },
    });

    if (result) {
      setStorageData(result);
      setSynced(result.last_sync_count);
    }

    setSyncing(false);
  }, [storage]);

  useEffect(() => {
    setStorageData(storage);
  }, [storage]);

  const notSyncedYet = synced !== null || ["in_progress", "queued"].includes(storageData.status);

  return (
    <Card
      header={storageData.title ?? `Untitled ${storageData.type}`}
      extra={
        <Dropdown.Trigger
          align="right"
          content={
            <Menu size="compact" style={{ width: 110 }}>
              <Menu.Item onClick={() => onEditStorage(storageData)}>Edit</Menu.Item>
              <Menu.Item onClick={() => onDeleteStorage(storageData)}>Delete</Menu.Item>
            </Menu>
          }
        >
          <Button look="string" className="-ml-3" aria-label={t("misc.storage_options")}>
            <IconEllipsisVertical />
          </Button>
        </Dropdown.Trigger>
      }
    >
      <StorageSummary
        target={target}
        storage={storageData}
        className={rootClass.elem("summary").toClassName()}
        storageTypes={storageTypes}
      />
      {/* Source connections: will these files load and play in the browser? */}
      {!target && <StorageHealth storage={storageData} rootClass={rootClass} />}
      <div className={rootClass.elem("sync").toClassName()}>
        <div className="mt-base">
          <Button
            look="outlined"
            waiting={syncing}
            onClick={startSync}
            disabled={notSyncedYet}
            aria-label={t("misc.sync_storage")}
          >
            {t("misc.sync_storage")}
          </Button>
          {notSyncedYet && (
            <div className={rootClass.elem("sync-count").toClassName()}>
              Syncing may take some time, please refresh the page to see the current status.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
